import {
  Stack,
  Avatar,
  Box,
  useMediaQuery, Divider
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronRight, Lectern } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useNavigate
} from "react-router-dom";

import {
  OwnerNameTypography,
  TitleTypography,
  BodyTypography,
  TopicCardTop } from "./TopicCard.styles";
import type { TopicCardProps } from "./TopicCard.types";
import {
  CardCategories,
  CommentsSection,
  CtaCommunityOverlay,
  TopicCardSignature
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";
import {
  useGetCreatedLabel,
  getReadingTime,
  secondsToMinuteslabel,
  stringAvatar
} from "../../utils/community";
import { BadgeBox } from "../Commons/BadgeBox";

export function TopicCard({
  ownerName,
  created,
  title,
  topic,
  body,
  ownerImage,
  image,
  commentsNumber,
  likes,
  id,
  subtitle,
  attachments,
  selectedFilter,
  selectedTopic,
  topicOwner
}: TopicCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [showComments, setShowComments] = useState(false);


  return (
    <CommonWidgetContainer
      tabIndex={ 0 }
      aria-label={ t("community_post_aria_label",{ subject:ownerName,post_title:title,time:useGetCreatedLabel(created)||"" }) }
      sx={ {
        width:"100% !important",
        maxWidth:"100%",
        borderRadius: "1rem",
        padding: "1.5rem",

        "& p": {
          color: theme.customColors.exploreCardText
        },
        "& span":{
          color:theme.customColors.exploreCardText
        },
        "& .icon path": {
          fill: `${theme.customColors.exploreCardText} !important`
        }
      } }
      hasSidePadding={ true }
      hoverBorderAccent={ false }
    >

      <TopicCardTop aria-hidden={ true } tabIndex={ -1 }>
        <Avatar
          alt={ `${t("avatar_user")} ${ownerName}` }
          { ...stringAvatar(ownerName) }
          src={ ownerImage }
          sx={ {
            alignSelf: "start",
            height: isMobile ? "32px" : "56px",
            width: isMobile ? "32px" : "56px"
          } }
        />
        <Stack
          gap={ 0.5 }
          flexGrow={ 1 }>
          <Stack
            direction={ "row" }
            justifyContent={ "space-between" }
            alignItems={ "center" }
          >
            <OwnerNameTypography>{ ownerName }</OwnerNameTypography>
            <BadgeBox
              background={ theme.customColors.backgroundSecondary }
              textColor={ "inherit" }
              border={ `1px solid ${theme.customColors.textTag}` }>
              <Stack
                direction={ "row" }
                alignItems={ "center" }
                gap={ "8px" }
              >
                <Lectern size={ 16 }/>
                <TypographyTextTag>
                  { t("talk") }
                </TypographyTextTag>
              </Stack>
            </BadgeBox>
          </Stack>
          <TopicCardSignature
            created={ useGetCreatedLabel(created) }
            readingTime={  secondsToMinuteslabel(
              getReadingTime(body)
              + Number(attachments.brightcove ? attachments.brightcove?.[0].duration : "0")
              + Number(attachments.document ? attachments.document?.[0].duration : "0")
            ) }
            size={ "medium" }
            color={ theme.customColors.exploreCardText }
          />
        </Stack>
      </TopicCardTop>

      <Divider/>

      { image && image.length > 0 &&
        <Box
          component="img"
          alt={ title }
          src={ image }
          sx={ {
            borderRadius: "12px",
            height: "auto",
            maxHeight: "20rem",
            objectFit: "cover",
            width: "100%"
          } }
        />
      }

      {
        title || (topic && topic.length > 0) || subtitle ?
          <Stack gap={ 1.5 }>
            { title ? <TitleTypography>{ title }</TitleTypography> : null }
            { subtitle ?
              <BodyTypography tabIndex={ 0 }>{ subtitle }</BodyTypography> :
              null }
            { topic && topic.length > 0 ?
              <CardCategories
                categories={ topic }
                sx={ {
                  "div":{
                    borderColor: theme.customColors.exploreCardText,
                    color: theme.customColors.exploreCardText
                  }
                } }
              /> :
              null }
          </Stack> :
          null
      }

      <ButtonDigited
        typology="tertiary"
        sx={ {
          padding: 0,
          width: "fit-content"
        } }
        onClick={ () => {
          navigate(
            `/community/${id}`,
            {
              state: {
                viewComment: false
              }
            }
          );
        }
        }
      >
        <Box display="flex" alignItems="center" gap="0.44rem" fontSize={ "0.75rem" } fontWeight={ 600 }>
          { t("read_more") }
          <ChevronRight fontSize={ "0.75rem" } width={ "1rem" } />
        </Box>
      </ButtonDigited>

      <Divider/>

      <CtaCommunityOverlay
        id={ id }
        comments={ commentsNumber }
        likes={ likes }
        commentsOpened={ showComments }
        commentsClickAction={ () => {
          setShowComments(!showComments);
        } }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
      />

      <CommentsSection
        topicOwnerId={ topicOwner }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
        discussionId={ id }
        enabled={ showComments }
        className={ showComments ? "visible" : "" } />

    </CommonWidgetContainer>
  );


}
